import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Toolbar,
  Typography,
  Stack,
  Button,
  Snackbar,
  Grid,
  Chip,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

function StudentResult() {
  const [results, setResults] = useState([]);
  const [studentResults, setStudentResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [student, setStudent] = useState(undefined);
  const [studentLoading, setStudentLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [sgpa, setSgpa] = useState(null);
  const [percent, setPercent] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [downloading, setDownloading] = useState(false);

  const user = useMemo(() => {
    try {
      const storedUser = sessionStorage.getItem("user");
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (err) {
      return null;
    }
  }, []);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const showSnackbar = (msg, sev = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(sev);
    setSnackbarOpen(true);
  };

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const formatExamMonthYear = (month, year) => {
    if (!month) return "";
    const monthText = String(month);
    const yearText = year ? String(year) : "";
    return yearText && !monthText.includes(yearText) ? `${monthText}-${yearText}` : monthText;
  };

  const rows = useMemo(() => {
    const groups = new Map();

    studentResults.forEach((result, index) => {
      const key = [
        result.college_id,
        result.exam_schedule_detail_id || "no-detail",
        result.year_number || "",
        result.semester_number || "",
        result.exam_type_name || "",
        result.exam_month || "",
      ].join("-");

      if (!groups.has(key)) {
        groups.set(key, {
          id: key || index,
          college_id: result.college_id,
          exam_schedule_detail_id: result.exam_schedule_detail_id,
          year_number: result.year_number,
          semester_number: result.semester_number,
          exam_type_name: result.exam_type_name,
          exam_month: result.exam_month || result.passed_month,
        });
      }
    });

    return Array.from(groups.values());
  }, [studentResults]);

  const columns = [
    {
      field: "id",
      headerName: "S.No",
      width: 80,
      valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
    },
    {
      field: "year_number",
      headerName: "StudyYear",
      width: 110,
      valueGetter: (params) => params.row.year_number || "",
    },
    {
      field: "semester_number",
      headerName: "Semester",
      width: 110,
      valueGetter: (params) => params.row.semester_number || "",
    },
    {
      field: "exam_type_name",
      headerName: "ExamType",
      width: 120,
      valueGetter: (params) => params.row.exam_type_name || "",
    },
    {
      field: "month_year",
      headerName: "MonthYear",
      width: 180,
      valueGetter: (params) => formatExamMonthYear(params.row.exam_month, params.row.exam_year),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          size="medium"
          onClick={() => handleShowResult(params.row)}
          sx={{
            color: "#fff",
            "&:hover": { color: "#fff" },
            "& .MuiButton-startIcon, & .MuiButton-endIcon": { color: "#fff" },
          }}
        >
          Show Result
        </Button>
      ),
    },
  ];

  useEffect(() => {
    if (!user?.client_id || !user?.user_email) {
      setStudent(null);
      setStudentLoading(false);
      setLoading(false);
      return;
    }

    setStudentLoading(true);
    const fetchStudent = async () => {
      try {
        const url = `${API_BASE_URL}/api/students/by-email/${user.client_id}/${encodeURIComponent(
          user.user_email
        )}?college_id=${encodeURIComponent(user.college_id || "")}`;
        const res = await axios.get(url, { headers: getAuthHeaders() });
        const data = res.data?.data || res.data || null;
        setStudent(data && data.student_id ? data : null);
      } catch (err) {
        setStudent(null);
      } finally {
        setStudentLoading(false);
      }
    };
    fetchStudent();
  }, [user?.client_id, user?.user_email, API_BASE_URL]);

  useEffect(() => {
    if (student === null) {
      setLoading(false);
      setStudentResults([]);
      return;
    }
    if (!student || !user?.client_id) return;

    const fetchStudentResults = async () => {
      setLoading(true);
      try {
        const resultUrl = `${API_BASE_URL}/api/studentresult/student-final-results?student_id=${student.student_id}&client_id=${user.client_id}`;
        const resultRes = await axios.get(resultUrl, { headers: getAuthHeaders() });
        setStudentResults(resultRes.data?.data || []);
      } catch (err) {
        setStudentResults([]);
        showSnackbar("Error fetching student results", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchStudentResults();
  }, [student, user?.client_id, API_BASE_URL]);

  const isFailStatus = (row) => {
    const status = String(row?.status || "")
      .trim()
      .toUpperCase();
    const grade = String(row?.grade || "")
      .trim()
      .toUpperCase();

    return (
      status.includes("FAIL") ||
      status.includes("ABSENT") ||
      status === "AB" ||
      grade === "F" ||
      grade === "AB"
    );
  };

  const isCoursePassed = (row) => {
    if (isFailStatus(row)) return false;

    const status = String(row?.status || "")
      .trim()
      .toUpperCase();

    if (status === "PASS" || status === "PASSED") return true;

    const gradePoints = Number(row?.grade_point ?? row?.grade_points);

    return Number.isFinite(gradePoints) && gradePoints > 0;
  };

  const getMaximumMarks = (row) => {
    // Use the already calculated maximum marks if available.
    const storedMaximum = Number(row?.max_marks);

    if (
      row?.max_marks !== null &&
      row?.max_marks !== undefined &&
      row?.max_marks !== "" &&
      Number.isFinite(storedMaximum) &&
      storedMaximum > 0
    ) {
      return storedMaximum;
    }

    // Otherwise calculate from exam course maximum marks.
    const internalMaximum = Number(row?.internal_maximum ?? row?.examCourse?.internal_maximum ?? 0);

    const externalMaximum = Number(row?.external_maximum ?? row?.examCourse?.external_maximum ?? 0);

    const maximum = internalMaximum + externalMaximum;

    return maximum > 0 ? maximum : null;
  };

  const calculatePercentage = (resultRows) => {
    if (!resultRows || resultRows.length === 0) return null;

    // Do not calculate percentage if any course is failed.
    const allCoursesPassed = resultRows.every(isCoursePassed);

    if (!allCoursesPassed) return null;

    let totalObtainedMarks = 0;
    let totalMaximumMarks = 0;

    for (const row of resultRows) {
      const obtainedMarks =
        row?.total_marks === null || row?.total_marks === undefined || row?.total_marks === ""
          ? NaN
          : Number(row.total_marks);

      const maximumMarks = getMaximumMarks(row);

      // Every course must have valid marks and maximum marks.
      if (!Number.isFinite(obtainedMarks) || !Number.isFinite(maximumMarks) || maximumMarks <= 0) {
        return null;
      }

      totalObtainedMarks += obtainedMarks;
      totalMaximumMarks += maximumMarks;
    }

    if (totalMaximumMarks <= 0) return null;

    return Number(((totalObtainedMarks / totalMaximumMarks) * 100).toFixed(2));
  };

  function handleShowResult(resultGroup) {
    const resultRows = studentResults.filter(
      (result) =>
        String(result.exam_schedule_detail_id || "") ===
          String(resultGroup.exam_schedule_detail_id || "") &&
        String(result.year_number || "") === String(resultGroup.year_number || "") &&
        String(result.semester_number || "") === String(resultGroup.semester_number || "") &&
        String(result.exam_type_name || "") === String(resultGroup.exam_type_name || "") &&
        String(result.exam_month || result.passed_month || "") ===
          String(resultGroup.exam_month || "")
    );

    setSelectedResult(resultGroup);
    setModalOpen(true);
    setResults(resultRows);

    // Calculate selected semester examination percentage.
    const calculatedPercentage = calculatePercentage(resultRows);

    setPercent(calculatedPercentage);

    const sgpaRow = resultRows.find((row) => row.sgpa !== undefined && row.sgpa !== null);

    setSgpa(sgpaRow?.sgpa ?? null);

    if (!resultRows.length) {
      showSnackbar("No published result found for this exam", "warning");
    }
  }

  const handleDownloadPDF = async () => {
    if (!results || results.length === 0) {
      showSnackbar("No result data to download", "warning");
      return;
    }

    setDownloading(true);
    try {
      const params = new URLSearchParams({
        student_id: results[0]?.student_id || student?.student_id,
        exam_schedule_detail_id: selectedResult?.exam_schedule_detail_id || "",
        year_number: selectedResult?.year_number || "",
        semester_number: selectedResult?.semester_number || "",
        client_id: user?.client_id,
      }).toString();

      window.open(`${API_BASE_URL}/api/studentresult/download-result-pdf?${params}`, "_blank");
      showSnackbar("PDF is being downloaded", "success");
    } catch (err) {
      showSnackbar("Error downloading PDF", "error");
    } finally {
      setDownloading(false);
    }
  };
  const getOrdinal = (num) => {
    const n = Number(num);
    if (!n) return { number: "", suffix: "" };

    const lastDigit = n % 10;
    const lastTwoDigits = n % 100;

    let suffix = "th";

    if (lastTwoDigits < 11 || lastTwoDigits > 13) {
      if (lastDigit === 1) suffix = "st";
      else if (lastDigit === 2) suffix = "nd";
      else if (lastDigit === 3) suffix = "rd";
    }

    return { number: n, suffix };
  };

  // Get exam title for display
  const getExamTitle = () => {
    if (!selectedResult || !results.length) return "";

    const year = selectedResult.year_number;

    const sem = selectedResult.semester_number;

    const examType = results[0]?.exam_type_name || "";

    const yearText = getOrdinal(year);
    const semText = getOrdinal(sem);

    return (
      <>
        {yearText.number}
        <sup>{yearText.suffix}</sup> YEAR {semText.number}
        <sup>{semText.suffix}</sup> SEMESTER {examType.toUpperCase()} EXAMINATIONS
      </>
    );
  };

  const firstResult = results[0] || {};

  const hasFail = results.some(isFailStatus);

  const allCoursesPassed = results.length > 0 && results.every(isCoursePassed);

  const displayStatus = results.length
    ? hasFail
      ? "FAIL"
      : allCoursesPassed
      ? "PASS"
      : firstResult.status || firstResult.result || ""
    : "";

  const statusColor = displayStatus === "PASS" ? "#14883e" : "#c62828";

  const statusBackground = displayStatus === "PASS" ? "#e7f6ec" : "#fdeaea";

  return (
    <Box p={2} sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <Paper
        elevation={1}
        sx={{
          width: "100%",
          maxWidth: 1100,
          margin: "0 auto",
          overflowX: "auto",
          boxSizing: "border-box",
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            STUDENT RESULTS
          </Typography>
        </Toolbar>
        <Box p={2} sx={{ width: "100%", boxSizing: "border-box" }}>
          <Stack spacing={2}>
            {studentLoading ? (
              <Typography variant="h6">Loading student info...</Typography>
            ) : student === null ? (
              <Typography variant="h6" color="error">
                Student Details are not found.
              </Typography>
            ) : (
              <>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  autoHeight
                  loading={loading}
                  disableRowSelectionOnClick
                  hideFooter
                  sx={{ width: "auto", minWidth: 900 }}
                />
                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  <b>Note:</b> In case of any deviation, the concern students may contact the COE
                  (Examination Cell) immediately.
                </Typography>
              </>
            )}
          </Stack>
        </Box>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>

      {/* Modal for detailed result */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2,
            borderBottom: "1px solid #dfe5ee",
            background: "#f8fafc",
          }}
        >
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} md={2.5}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 800, color: "#0f5fb8", letterSpacing: 0 }}
              >
                EXAM RESULTS
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              {results && results.length > 0 && (
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    color: "#1267c7",
                    textAlign: "center",
                    fontSize: { xs: 17, md: 20 },
                    lineHeight: 1.25,
                  }}
                >
                  {getExamTitle()}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={2.5} sx={{ display: "flex", justifyContent: "flex-end" }}>
              {results && results.length > 0 && (
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleDownloadPDF}
                  disabled={downloading}
                  sx={{
                    bgcolor: "#dc3545",
                    "&:hover": { bgcolor: "#c82333" },
                    boxShadow: "0 6px 14px rgba(220,53,69,0.22)",
                    fontWeight: 700,
                    textTransform: "none",
                    color: "#fff",
                    borderRadius: 1,
                    px: 2,
                  }}
                >
                  {downloading ? "Preparing PDF..." : "Download PDF"}
                </Button>
              )}
            </Grid>
          </Grid>
        </DialogTitle>

        <DialogContent sx={{ p: 0, background: "#f6f8fb" }}>
          {selectedResult && results.length === 0 && (
            <Typography variant="body1" sx={{ p: 3 }}>
              No published result found for this exam.
            </Typography>
          )}
          {selectedResult && results.length > 0 && (
            <Box sx={{ p: 3 }}>
              <Box
                sx={{
                  border: "1px solid #dce3ec",
                  borderRadius: 1,
                  mb: 2.5,
                  background: "#fff",
                  overflow: "hidden",
                }}
              >
                <Grid container>
                  {[
                    ["Student Roll No", firstResult.student_rollno],
                    ["Student Name", firstResult.student_name],
                    ["Branch Name", firstResult.branch_name],
                    ["Student ID", firstResult.student_id],
                  ].map(([label, value]) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      key={label}
                      sx={{
                        p: 2,
                        borderRight: { md: "1px solid #edf1f6" },
                        borderBottom: { xs: "1px solid #edf1f6", md: 0 },
                        "&:last-of-type": { borderRight: 0 },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#69768a",
                          fontWeight: 800,
                          display: "block",
                          mb: 0.5,
                        }}
                      >
                        {label}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#243047", fontWeight: 700 }}>
                        {value || "-"}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <DataGrid
                rows={results.map((r, index) => ({
                  ...r,
                  id: r.result_id || index,
                  subject_name: r.course_name,
                }))}
                columns={[
                  {
                    field: "sno",
                    headerName: "S.No",
                    width: 80,
                    valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
                  },
                  { field: "subject_name", headerName: "Course Name", flex: 1.5, minWidth: 200 },
                  {
                    field: "internal_marks",
                    headerName: "Internal",
                    width: 90,
                    align: "center",
                    headerAlign: "center",
                    valueFormatter: (params) =>
                      params.value !== undefined && params.value !== null
                        ? Number(params.value).toFixed(0)
                        : "-",
                  },
                  {
                    field: "external_marks",
                    headerName: "External",
                    width: 90,
                    align: "center",
                    headerAlign: "center",
                    valueFormatter: (params) =>
                      params.value !== undefined && params.value !== null
                        ? Number(params.value).toFixed(0)
                        : "-",
                  },
                  {
                    field: "total_marks",
                    headerName: "Total",
                    width: 90,
                    align: "center",
                    headerAlign: "center",
                    valueFormatter: (params) =>
                      params.value !== undefined && params.value !== null
                        ? Number(params.value).toFixed(0)
                        : "-",
                  },
                  {
                    field: "credits",
                    headerName: "Credits",
                    width: 80,
                    align: "center",
                    headerAlign: "center",
                  },
                  {
                    field: "grade",
                    headerName: "Grade",
                    width: 80,
                    align: "center",
                    headerAlign: "center",
                  },
                  {
                    field: "grade_point",
                    headerName: "Grade Points",
                    width: 110,
                    align: "center",
                    headerAlign: "center",
                    renderCell: (params) => (
                      <span>
                        {params.value !== undefined && params.value !== null
                          ? Number(params.value).toFixed(0)
                          : "-"}
                      </span>
                    ),
                  },
                  {
                    field: "status",
                    headerName: "Status",
                    width: 100,
                    align: "center",
                    headerAlign: "center",
                    renderCell: (params) => (
                      <Chip
                        label={params.value || "-"}
                        size="medium"
                        sx={{
                          color:
                            params.value === "PASS"
                              ? "#2e7d32"
                              : params.value === "FAIL"
                              ? "#d32f2f"
                              : "#000",
                          fontWeight: 800,
                          borderRadius: 1,
                          backgroundColor:
                            params.value === "PASS"
                              ? "#e8f5e9"
                              : params.value === "FAIL"
                              ? "#ffebee"
                              : "#f2f4f7",
                        }}
                      />
                    ),
                  },
                ]}
                autoHeight
                hideFooter
                disableRowSelectionOnClick
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? "result-row-even" : "result-row-odd"
                }
                sx={{
                  border: "1px solid #dce3ec",
                  borderRadius: 1,
                  background: "#fff",
                  overflow: "hidden",
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#eef3f8",
                    color: "#1f2a3d",
                    fontWeight: 800,
                    borderBottom: "1px solid #dce3ec",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: 800,
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid #edf1f6",
                  },
                  "& .result-row-even": {
                    backgroundColor: "#fff",
                  },
                  "& .result-row-odd": {
                    backgroundColor: "#fbfcfe",
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "#f3f8ff",
                  },
                }}
              />

              <Box
                sx={{
                  border: "1px solid #dce3ec",
                  borderRadius: 1,
                  mt: 2.5,
                  background: "#fff",
                  overflow: "hidden",
                }}
              >
                <Grid container>
                  {[
                    {
                      label: "Result",
                      value: displayStatus || "-",
                      chip: true,
                    },
                    {
                      label: "Semester Percentage",
                      value:
                        percent !== null && percent !== undefined
                          ? `${Number(percent).toFixed(2)}%`
                          : "-",
                    },
                    {
                      label: "SGPA",
                      value: sgpa || firstResult.overall_sgpa || "-",
                    },
                  ].map((item) => (
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      key={item.label}
                      sx={{
                        p: 2,
                        borderRight: { sm: "1px solid #edf1f6" },
                        "&:last-of-type": { borderRight: 0 },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#69768a",
                          fontWeight: 800,
                          display: "block",
                          mb: 0.75,
                        }}
                      >
                        {item.label}
                      </Typography>
                      {item.chip ? (
                        <Chip
                          label={item.value}
                          size="medium"
                          sx={{
                            color: statusColor,
                            backgroundColor: statusBackground,
                            fontWeight: 800,
                            borderRadius: 1,
                          }}
                        />
                      ) : (
                        <Typography
                          variant="body1"
                          sx={{
                            color: item.label === "SGPA" ? "#1267c7" : "#243047",
                            fontWeight: 800,
                          }}
                        >
                          {item.value}
                        </Typography>
                      )}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            borderTop: "1px solid #dfe5ee",
            background: "#fff",
          }}
        >
          <Button
            onClick={() => setModalOpen(false)}
            color="error"
            variant="contained"
            size="medium"
            sx={{ color: "#000", "&:hover": { color: "#000" }, borderRadius: 1, px: 3 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default StudentResult;
